import type { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { SidebarIcon } from '@blocksuite/icons/rc';

import type { AppSidebarService } from '../modules/app-sidebar';
import {
  areaSidebar$,
  toggleAreaSidebar,
} from '../modules/streamlain/native-app/area-sidebar';
import type { WorkbenchService } from '../modules/workbench';
import {
  AFFINE_LEFT_SIDEBAR_KEY_BINDING,
  AFFINE_RIGHT_SIDEBAR_KEY_BINDING,
} from './affine-keybindings';
import { registerAffineCommand } from './registry';

export function registerAffineLayoutCommands({
  t,
  appSidebarService,
  workbenchService,
}: {
  t: ReturnType<typeof useI18n>;
  appSidebarService: AppSidebarService;
  workbenchService?: WorkbenchService;
}) {
  const unsubs: Array<() => void> = [];
  // The sidebar of the area on screen; an area without one leaves the shortcut to the rail.
  const leftSidebarOpen = () =>
    areaSidebar$.value?.open ?? appSidebarService.sidebar.open$.value;
  unsubs.push(
    registerAffineCommand({
      id: 'affine:toggle-left-sidebar',
      category: 'affine:layout',
      icon: <SidebarIcon />,
      label: () =>
        leftSidebarOpen()
          ? t['com.streamlain.cmdk.affine.left-sidebar.collapse']()
          : t['com.streamlain.cmdk.affine.left-sidebar.expand'](),

      keyBinding: {
        binding: AFFINE_LEFT_SIDEBAR_KEY_BINDING,
      },
      run() {
        track.$.navigationPanel.$.toggle({
          type: leftSidebarOpen() ? 'collapse' : 'expand',
        });
        if (!toggleAreaSidebar()) appSidebarService.sidebar.toggleSidebar();
      },
    })
  );

  if (workbenchService) {
    unsubs.push(
      registerAffineCommand({
        id: 'affine:toggle-right-sidebar',
        category: 'affine:layout',
        icon: <SidebarIcon />,
        label: () =>
          workbenchService.workbench.sidebarOpen$.value
            ? t['com.streamlain.cmdk.affine.right-sidebar.collapse']()
            : t['com.streamlain.cmdk.affine.right-sidebar.expand'](),

        keyBinding: {
          binding: AFFINE_RIGHT_SIDEBAR_KEY_BINDING,
        },
        run() {
          workbenchService.workbench.toggleSidebar();
        },
      })
    );
  }

  return () => {
    unsubs.forEach(unsub => unsub());
  };
}
